/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "utils";

import type { PostHydrationLatch } from "pages/app/scenario-analysis/hooks/usePostHydrationLatch";
import type { SoilSightPersistence } from "pages/app/scenario-analysis/hooks/useSoilSightPersistence";
import { type ProjectRefinementsState, type ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { showSoilSightSaveFailed } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { toApiSettingsRefinements } from "pages/app/scenario-analysis/utils/scenarioApiMappers";

/** Open the field-reselection modal once per project, re-arming once the field is valid again. */
const useOpenReselectOncePerProject = (projectId: string | null, needsReselect: boolean, openReselect: () => void): void => {
  const promptedProjectRef = useRef<string | null>(null);
  useEffect(
    function openFieldReselectWhenMissing() {
      if (!projectId) return;
      if (needsReselect) {
        if (promptedProjectRef.current !== projectId) {
          promptedProjectRef.current = projectId;
          openReselect();
        }
      } else if (promptedProjectRef.current === projectId) {
        promptedProjectRef.current = null;
      }
    },
    [projectId, needsReselect, openReselect]
  );
};

type FieldReselectArgs = {
  klimFarmId: string;
  soilSightProjectId: string | null;
  /** From isFieldReselectNeeded, computed by the page before the recalculation hook that also reads it. */
  fieldReselectNeeded: boolean;
  fieldsForProjectFarmLoaded: boolean;
  openReselectModal: () => void;
  closeReselectModal: () => void;
  settings: ScenarioSettings;
  projectRefinements: ProjectRefinementsState;
  setProjectRefinements: Dispatch<SetStateAction<ProjectRefinementsState>>;
  saveSettingsNow: SoilSightPersistence["saveSettingsNow"];
  /** Drops the previous field's results once the new field is saved. */
  onFieldReplaced: () => void;
  postHydrationLatch: PostHydrationLatch;
  recalculate: () => Promise<unknown>;
};

/** Prompts for a new field when the project's field was deleted (or never set), before any run fails server-side. */
export const useFieldReselect = ({
  klimFarmId,
  soilSightProjectId,
  fieldReselectNeeded,
  fieldsForProjectFarmLoaded,
  openReselectModal,
  closeReselectModal,
  settings,
  projectRefinements,
  setProjectRefinements,
  saveSettingsNow,
  onFieldReplaced,
  postHydrationLatch,
  recalculate,
}: FieldReselectArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [isSavingFieldReselect, setIsSavingFieldReselect] = useState(false);
  const pendingReselectRecalcFieldRef = useRef<string | null>(null);

  // Querying before the fields load, or after the field is gone, 403s. Shared so consumers cannot drift.
  const isConnectedFieldUsable = fieldsForProjectFarmLoaded && !fieldReselectNeeded;

  useOpenReselectOncePerProject(soilSightProjectId, fieldReselectNeeded, openReselectModal);

  const handleFieldReselectConfirm = useCallback(
    async function handleFieldReselectConfirm(fieldId: string) {
      setIsSavingFieldReselect(true);
      // The full settings body, as the drawer sends, so the list cache's fieldId patch fires.
      const nextRefinements = { ...projectRefinements, location: { ...projectRefinements.location, fieldSelect: fieldId } };
      const body = toApiSettingsRefinements({ settings, projectRefinements: nextRefinements });
      try {
        if (!(await saveSettingsNow(body, { klimFarmId }))) return;
        setProjectRefinements(nextRefinements);
        // Dirty keeps the "recalculation needed" shell up until the new field's results land.
        onFieldReplaced();
        // Only on success: a failed save keeps the modal open so no project is left fieldless.
        closeReselectModal();
        pendingReselectRecalcFieldRef.current = fieldId;
      } catch (saveError) {
        showSoilSightSaveFailed(t, saveError);
      } finally {
        setIsSavingFieldReselect(false);
      }
    },
    [closeReselectModal, klimFarmId, onFieldReplaced, projectRefinements, saveSettingsNow, setProjectRefinements, settings, t]
  );

  // The project cache and local refinements take the new field on different renders; running before both agree hits the deleted field.
  useEffect(
    function runDeferredRecalcAfterFieldReselect() {
      const pendingFieldId = pendingReselectRecalcFieldRef.current;
      if (!pendingFieldId) return;
      if (fieldReselectNeeded) return;
      if (projectRefinements.location.fieldSelect !== pendingFieldId) return;
      pendingReselectRecalcFieldRef.current = null;
      // Supersedes the hydration auto-recalc.
      postHydrationLatch.markDecided();
      recalculate().catch(() => {});
    },
    [fieldReselectNeeded, postHydrationLatch, projectRefinements, recalculate]
  );

  useEffect(
    function clearPendingReselectOnProjectChange() {
      // Otherwise a pending reselect leaks into the next project and latches auto-recalc off.
      pendingReselectRecalcFieldRef.current = null;
    },
    [soilSightProjectId]
  );

  return { isConnectedFieldUsable, isSavingFieldReselect, handleFieldReselectConfirm, pendingReselectRecalcFieldRef };
};
